import { Layers } from 'lucide-react';

import { TableSkeleton } from '@/components/skeletons';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardDateRangePicker } from '@/components/ui/dashboard-date-range-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { STATS_PERIOD_OPTIONS } from '@/lib/template-labels';
import { templateStatRows } from '@/lib/template-stats';
import type { AdminTemplateDetail, StatsWindow } from '@/services/formTemplates';
import { EmptyBlock } from './EmptyBlock';
import { SalesSummaryCard } from './SalesSummaryCard';
import { StatText } from './StatText';
import { SummaryCardSkeleton } from './TemplateSkeletons';

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

interface TemplatePerformanceTabProps {
  detail: AdminTemplateDetail;
  /** The period asked for: the picker's value, put back to detail.days when a refetch fails. */
  days: StatsWindow;
  onDaysChange: (days: StatsWindow) => void;
  /** A refetch is in flight: the numbers on screen belong to the previous period, so they show as skeletons. */
  refreshing: boolean;
}

export function TemplatePerformanceTab({ detail, days, onDaysChange, refreshing }: TemplatePerformanceTabProps) {
  const totals = templateStatRows(detail.stats);
  const versions = [...detail.versions].sort((a, b) => b.versionNo - a.versionNo);
  // Version and Published, then one column per stat.
  const versionColumns = totals.length + 2;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <DashboardDateRangePicker
          value={String(days)}
          onChange={(value) => onDaysChange(Number(value) as StatsWindow)}
          options={STATS_PERIOD_OPTIONS}
          tooltipMessage="Imports in the selected period, staff imports left out. Activity on a copy counts from its import to now."
        />
      </div>

      {refreshing ? (
        <SummaryCardSkeleton />
      ) : (
        <SalesSummaryCard summary={detail.summary} days={detail.days} scope="template" />
      )}

      <Card className="py-0 gap-0">
        <CardHeader className="px-5 py-4 gap-0 border-b">
          <CardTitle className="text-base font-semibold">All versions</CardTitle>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {totals.map((row) => (
              <div key={row.key} className="rounded-lg bg-muted/50 p-3">
                <div className="text-sm text-muted-foreground">{row.label}</div>
                {refreshing ? (
                  <Skeleton className="h-7 w-[60px] my-px" />
                ) : (
                  <div className="text-2xl font-bold leading-tight tabular-nums">
                    <StatText value={row.value} />
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-0.5">{row.hint}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="py-0 gap-0">
        <CardHeader className="px-5 py-4 gap-0 border-b">
          <CardTitle className="text-base font-semibold">By version</CardTitle>
          <CardDescription>Each copy is counted under the version it was imported from</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Version</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Published</TableHead>
                  {totals.map((row) => (
                    <TableHead key={row.key} className="text-center whitespace-nowrap">
                      {row.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {refreshing && versions.length > 0 ? (
                  <TableSkeleton columns={versionColumns} rows={Math.min(versions.length, 5)} />
                ) : (
                  versions.map((version) => (
                    <TableRow key={version.versionNo}>
                      <TableCell className="whitespace-nowrap font-medium">
                        <span className="inline-flex items-center gap-2">
                          {`v${version.versionNo}`}
                          {version.isCurrent && detail.status === 'published' && <Badge tone="success">Live</Badge>}
                        </span>
                      </TableCell>
                      <TableCell className="text-center whitespace-nowrap text-muted-foreground">
                        {dateFormat.format(new Date(version.publishedAtUtc))}
                      </TableCell>
                      {templateStatRows(version.stats).map((row) => (
                        <TableCell key={row.key} className="text-center tabular-nums whitespace-nowrap">
                          <StatText value={row.value} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Under the header row, not in a row of the table: the table is wider than the card and scrolls,
              so a cell would centre the block over all its columns. */}
          {versions.length === 0 && (
            <div className="flex min-h-48 items-center justify-center px-5 py-6 text-center">
              <EmptyBlock
                icon={Layers}
                title="No version yet"
                description="Publish a version to see numbers per version."
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
